<template>
  <ModalValueHelp
    :context="integrationLogHistoryContext"
    :attrs="integrationLogHistoryAttrs"
  >
    <template #context="{ value }">
      <div class="overflow-wrap text-width-100">
        {{ value || '-' }}
      </div>
    </template>
    
    <template #url="{ value }">
      <div class="overflow-wrap text-width-150">
        {{ value || '-' }}
      </div>
    </template>
    
    <template #response="{ value, item }">
      <div class="d-flex align-items-center gap-2">
        <div class="text-width-200">
          <span
            class="integration-log-status-badge"
            :class="getStatusBadgeClass(item?.status_code)"
          >
            {{ getStatusBadgeLabel(item?.status_code) }}
          </span>

          <div class="text-wrap clamp-4-lines mt-2">
            {{ value || '-' }}
          </div>
        </div>
        <McButton
          icon="icon-outline_copy"
          text=""
          variant="secondary"
          size="sm"
          @click="handleCopy(value)"
        />
      </div>
    </template>
    
    <template #payload="{ value }">
      <div class="d-flex align-items-center gap-2">
        <div class="text-wrap clamp-4-lines text-width-200">
          {{ value || '-' }}
        </div>
        <McButton
          icon="icon-outline_copy"
          text=""
          variant="secondary"
          size="sm"
          @click="handleCopy(value)"
        />
      </div>
    </template>

    <template #created_at="{ value }">
      <template v-if="value">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="teal-200">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <template v-else>
        -
      </template>
    </template>

    <template #custom_action="{ item }">
      <template v-if="hasCustomFeatureSamator && item?.can_be_retried">
        <McButton
          :text="$t('master.list.fleet.integrationLogHistoryModal.action.retry')"
          icon="icon-outline_refresh"
          :disabled="isLoading"
          @click="handleRetry(item?.request_log_id)"
        />
      </template>
      <template v-else>-</template>
    </template>
  </ModalValueHelp>
</template>
<script>
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'
import McButton from '@/components/McButton'

import * as Integration from '@/services/rest-api/lib/integration/log-history'
import { formatIndexDate } from '@/helpers/tzDate'

export default {
  components: {
    ModalValueHelp,
    McButton,
  },
  mixins: [Helper],
  props: {
    module: {
      type: String,
      required: true,
    },
    identifier: {
      type: [String, Number],
      required: true,
    },
    hasCustomFeature: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isLoading: false,
      integrationLogHistoryContext: {
        label: this.$t('master.list.fleet.integrationLogHistoryModal.title'),
        model: {},
        blurHandler: () => {},
      },
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    integrationLogHistoryAttrs() {
      return {
        name: 'integration-log',
        modalTitle: this.$t('master.list.fleet.integrationLogHistoryModal.title'),
        tableFields: [
          {
            label: this.$t('master.list.fleet.integrationLogHistoryModal.fields.title'),
            key: 'context',
            name: 'context',
            isSortable: false,
            isSlot: true,
            tdClass: 'vertical-align-middle',
          },
          {
            label: 'Payload',
            key: 'payload',
            name: 'payload',
            isSortable: false,
            isSlot: true,
            tdClass: 'vertical-align-middle',
          },
          {
            label: this.$t('master.list.fleet.integrationLogHistoryModal.fields.response'),
            key: 'response',
            name: 'response',
            formatter: (val, key, item) => {
              return JSON.stringify(val, null, 2)
            },
            isSortable: false,
            isSlot: true,
            tdClass: 'vertical-align-middle',
          },
          {
            isShow: this.hasCustomFeature && (this.hasCustomFeatureSamator || this.hasCustomFeatureMGM),
            label: this.$t('master.list.fleet.integrationLogHistoryModal.fields.action'),
            key: 'identifier',
            name: 'custom_action',
            isSortable: false,
            isSlot: true,
            tdClass: 'action-wrapper vertical-align-middle',
          },
          {
            label: this.$t('master.list.fleet.integrationLogHistoryModal.fields.integrationDate'),
            key: 'created_at',
            name: 'created_at',
            isSortable: false,
            isSlot: true,
            tdClass: 'vertical-align-middle',
          },
          {
            label: this.$t('master.list.fleet.integrationLogHistoryModal.fields.url'),
            key: 'url',
            name: 'url',
            isSortable: false,
            isSlot: true,
            tdClass: 'vertical-align-middle',
          },
        ].filter((field) => field?.isShow !== false),
        listGetter: async (opts) => {
          const filter = {
            ...opts,
            filters: [
              `module eq ${this.module}`,
              `identifier eq ${this.identifier}`,
              ...(this.hasCustomFeatureSamator ? [
                'disable_retry_for_old_request eq true',
                'include_identifier_with_postfix eq true',
              ] : []),
            ],
          }
          
          const result = await Integration.getLogHistory(filter)

          return result
        },
        trackBy: 'request_log_id',
        cancelOnly: true,
        selectable: false,
      }
    },
    hasCustomFeatureSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    hasCustomFeatureMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
  },
  methods: {
    formatIndexDate,
    getStatusBadgeClass(statusCode) {
      return String(statusCode).startsWith('2') ? 'is-success' : 'is-failed'
    },
    getStatusBadgeLabel(statusCode) {
      return String(statusCode).startsWith('2') ? `Sukses (${statusCode})` : `Gagal (${statusCode})`
    },
    handleCopy(value) {
      navigator.clipboard.writeText(value)
      this.$mcToast.success(this.$t('global.action.copySuccess'))
    },
    async handleRetry(id) {
      try {
        this.isLoading = true
        if (!id) {
          this.$mcToast.error(this.$t('master.list.fleet.integrationLogHistoryModal.toast.retryFailedIdNotFound'))
          return
        }
  
        const res = await Integration.retry(id)

        if (res.errors) {
          this.$mcToast.error(this.$t('master.list.fleet.integrationLogHistoryModal.toast.retryFailed'))
          return
        }

        this.$root.$emit('refresh-value-help', 'integration-log')
        this.$mcToast.success(this.$t('master.list.fleet.integrationLogHistoryModal.toast.retrySuccess'))
      } catch {
        this.$mcToast.error(this.$t('master.list.fleet.integrationLogHistoryModal.toast.somethingWentWrong'))
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
.text-width-100 {
  width: 100px;
}

.text-width-150 {
  width: 150px;
}

.text-width-200 {
  width: 200px;
}

.clamp-4-lines {
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 4;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.overflow-wrap {
  overflow-wrap: break-word;
  white-space: normal;
}

.vertical-align-middle {
  vertical-align: middle !important;
}

.integration-log-status-badge {
  display: inline-block;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
}

.integration-log-status-badge.is-success {
  background-color: #dff4e5;
  color: #1e7b3a;
}

.integration-log-status-badge.is-failed {
  background-color: #fde6e4;
  color: #b9382d;
}

.action-wrapper {
  min-width: 150px;
  .mc-button {
    padding: 4px 8px 4px 8px;
  }
}
</style>
